import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, CardBody, Modal } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getStatusText } from "../../Common/Utils/statusUtils";
import { formatDate, formatDateTime } from "../../Common/Utils/dateUtils";
import { getUserStatusClass } from "../../Common/Utils/adminViewUtils";
import search from "../../assets/images/search-lg.svg";
import { getUser, deleteUser } from "../../../redux/user/action";
import CardHeader from "../../Common/CardHeader/CardHeader";
import "../../Common/Styles/Listing.css";
import "../../Common/Styles/AdminEnhancements.css";
import UserDelete from "./UserDelete";
import CategoryFilter from "../Category/CategoryFilter";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import UserFilter from "./UserFilter";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";

const UserList = () => {
  const [params, setParams] = useState({
    page: 1,
    search_key: "",
    status: "",
    type: 1,
    start_date: "",
    end_date: "",
    testing_account_filter: "hide",
  });
  const [openFilter, setOpenFilter] = useState(false);
  const [modal, setModal] = useState(false);
  const [currentComponent, setCurrentComponent] = useState({});
  const [loading, setLoading] = useState(false);
  const [componentList, setComponentList] = useState([]);
  const [modals, setModals] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userlist = useSelector(({ user: { userlist } }) => userlist);

  useEffect(() => {
    if (userlist) {
      setComponentList(userlist.users || []);
    }
  }, [userlist]);

  useEffect(() => {
    dispatch(getUser(params, setLoading));
  }, [dispatch, params]);

  const summary = useMemo(() => {
    const active = componentList.filter((item) => Number(item?.status) === 1).length;
    const inactive = componentList.filter((item) => Number(item?.status) === 2).length;
    const blocked = componentList.filter((item) => Number(item?.status) === 3).length;
    const withRecentLogin = componentList.filter((item) => item?.last_login_date).length;

    return [
      {
        label: "Customers on this page",
        value: componentList.length,
        subtext: userlist?.pagination_meta?.total
          ? `${userlist.pagination_meta.total} total records`
          : "Current page view",
      },
      { label: "Active", value: active, subtext: "Ready to use" },
      { label: "Inactive", value: inactive, subtext: "Need review or reactivation" },
      { label: "Blocked", value: blocked, subtext: "Access currently restricted" },
      { label: "Recent login", value: withRecentLogin, subtext: "Has login history" },
    ];
  }, [componentList, userlist]);

  const deleteComponent = () => {
    if (currentComponent && currentComponent.id) {
      dispatch(deleteUser(currentComponent.id, setLoading, params));
    } else {
      console.error("No valid ID found for the component to delete.");
    }
  };

  const addComponent = () => {
    navigate("/user/add");
  };

  const updateComponent = (id) => {
    navigate(`/user/update/${id}`);
  };

  return (
    <Card className="py-3">
      <div
        onClick={() => setOpenFilter(false)}
        className={openFilter ? `overlay show` : "overlay"}
      ></div>
      <Modal isOpen={modals} toggle={() => setModals(!modals)}>
        <UserFilter
          modal={modals}
          setModal={setModals}
          params={params}
          setParams={setParams}
        />
      </Modal>
      <CategoryFilter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        params={params}
        setParams={setParams}
      />
      <CardHeader
        headerTitle="Customer List"
        params={params}
        setParams={setParams}
        addmodel={addComponent}
        search={search}
        setOpenFilter={setOpenFilter}
        modals={modals}
        setModals={setModals}
      />
      <div className="px-3">
        <div className="admin-toolbar">
          <div className="admin-toolbar-note">
            A cleaner customer overview with consistent dates and quick status visibility.
          </div>
        </div>
        <div className="admin-summary-grid">
          {summary.map((item) => (
            <div className="admin-summary-card" key={item.label}>
              <div className="admin-summary-label">{item.label}</div>
              <div className="admin-summary-value">{item.value}</div>
              <div className="admin-summary-subtext">{item.subtext}</div>
            </div>
          ))}
        </div>
      </div>
      {loading ? (
        <FullScreenLoader />
      ) : (
        <CardBody className="table-sec px-3">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Company</th>
                <th scope="col">Customer</th>
                <th scope="col">Contact</th>
                <th scope="col">Created</th>
                <th scope="col">Last Login</th>
                <th scope="col">Status</th>
                <th scope="col">Account Type</th>
                <th scope="col" className="text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {componentList.length > 0 ? (
                componentList.map((item, index) => (
                  <tr key={item?.id || index}>
                    <td>{index + 1 + (params?.page - 1) * 10}</td>
                    <td>
                      <div>{item?.company_name || "--"}</div>
                      <div className="admin-inline-note">ID: {item?.id || "--"}</div>
                    </td>
                    <td>
                      <div>{item?.full_name || "--"}</div>
                      <div className="admin-inline-note">{item?.email_id || "--"}</div>
                    </td>
                    <td>
                      <div>
                        {item?.country_code} {item?.mobile_number}
                      </div>
                      <div className="admin-inline-note">Primary contact</div>
                    </td>
                    <td>{formatDate(item?.created_on)}</td>
                    <td>{formatDateTime(item?.last_login_date)}</td>
                    <td>
                      <span className={`admin-chip admin-chip--${getUserStatusClass(item?.status)}`}>
                        {getStatusText(item?.status)}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-chip${item?.is_testing_account ? " admin-chip--warning" : " admin-chip--success"}`}>
                        {item?.is_testing_account ? "Testing Account" : "Customer"}
                      </span>
                    </td>
                    <td>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ columnGap: "10px" }}
                      >
                        <button
                          className="border-button"
                          onClick={() => updateComponent(item?.id)}
                        >
                          <span>Update</span>
                        </button>
                        <button
                          className="background-button"
                          onClick={() => {
                            setModal(true);
                            setCurrentComponent(item);
                          }}
                        >
                          <span>Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="text-center">
                    No Customer Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <CommonPagination
            params={params}
            setParams={setParams}
            pagination_meta={userlist?.pagination_meta}
          />
        </CardBody>
      )}
      <Modal isOpen={modal} toggle={() => setModal((state) => !state)}>
        <UserDelete
          modalTitle="Customer"
          setModal={setModal}
          currentComponent={currentComponent}
          deleteComponent={deleteComponent}
        />
      </Modal>
    </Card>
  );
};

export default UserList;
